import { useCallback, useContext, useEffect, useState } from 'react'
import { animated, useSpring } from '@react-spring/web'
import { useNavigate } from 'react-router-dom'
import { Box, Skeleton, Typography } from '@mui/material'
import styles from '../../../styles/energyFlow.module.css'
import { DataContext, DataDispatchContext } from '../../../contexts/data/dataContext'
import { clickAnalyticsEF } from '../../common/util/util'

export default function EnergyFlowDiagram() {
  const dispatch: any = useContext(DataDispatchContext)
  const data: any = useContext(DataContext)
  const [ suffix, setSuffix ] = useState({
    grid: '',
    solar: ''
  })
  const navigate = useNavigate()
  const [imgs, api] = useSpring(
    () => ({
      from: {
        src: '/images/dashboard/Battery0.svg',
        delay: 0
      },
      to: { 
        src: `/images/dashboard/Battery1.svg`, 
        delay: 200 
      }
    }), []
  )

  const batteryImages = useCallback((status: any) => {
    const suffix = status.disCharging?'A':''
    const imgList = [
      { i:1, src: `/images/dashboard/Battery${suffix}1.svg`, delay: 2000 },
      { i:2, src: `/images/dashboard/Battery${suffix}2.svg`, delay: 2000 },
      { i:3, src: `/images/dashboard/Battery${suffix}3.svg`, delay: 2000 },
      { i:4, src: `/images/dashboard/Battery${suffix}4.svg`, delay: 2000 },
      { i:5, src: `/images/dashboard/Battery${suffix}5.svg`, delay: 2000 },
      { i:0, src: `/images/dashboard/Battery${suffix}0.svg`, delay: 2000 },
    ]
    .filter((img: any)=>{
      if(status.charging || status.disCharging) {
        if(status.disCharging) {
          return img.i<(status.minLevel+1)
        }
        return img.i>=(status.minLevel-1)
      } 
      return img.i===status.minLevel
    })
    if(imgList.length>1) {
      imgList.sort((a:any, b:any)=>{
        if(status.disCharging) {
          return b.i-a.i
        }
        return a.i-b.i
      })
    }
    // imgList[0].delay = 200;
    return imgList
  },[])

  useEffect(()=>{
    const imgStatus = ()=>{
      const flows = data?.dashboard?.energyFlow?.flows || {}
      const charge = data?.dashboard?.energyFlow?.battery?.charged || 0;
      let minLevel = Math.round(charge/20)
      const charging = flows['solartobattery']>0 || 
                        flows['gridtobattery']>0;
      const disCharging = flows['batterytohome']>0 || 
                          flows['batterytogrid']>0;
      const gridImporting = flows['gridtobattery']>0 || 
                            flows['gridtohome']>0;
      const solarActive = flows['solartobattery']>0 || 
                          flows['solartohome']>0 || 
                          flows['solartogrid']>0;
      return {
        charging,
        disCharging,
        minLevel,
        gridImporting,
        solarActive
      }
    }
    const status: any = imgStatus()
    setSuffix({ 
      solar: status.solarActive?'A':'', 
      grid: status.gridImporting?'A':'', 
    })
    // battery animation
    api.start({
      loop: true,
      config: { duration: 0 },
      to: batteryImages(status),
    })
  
  },[
    api,
    batteryImages,
    data?.dashboard?.energyFlow
  ])

  const isFlowActive = (from: string, to: string) => {
    const flow = `${from}to${to}`  
    if(data?.dashboard?.energyFlow?.flows) {
      const active =  data?.dashboard?.energyFlow?.flows[flow]>0
      return active;
    } 
    return false;
  }

  // The grid<->battery path is drawn from the grid end to the battery end.
  // 'animated-path' moves the dashes towards the path start (battery -> grid),
  // 'animated-path-reverse' moves them towards the path end (grid -> battery).
  const gridBatteryFlowClass = () => {
    if(isFlowActive('battery', 'grid')) return `${styles['animated-path']}`
    if(isFlowActive('grid', 'battery')) return `${styles['animated-path-reverse']}`
    return ''
  }
  const isGridBatteryActive = () => isFlowActive('battery', 'grid') || isFlowActive('grid', 'battery')

  const handleReportView = (selected: string) => {
    dispatch({
        type: 'setReportSelected',
        selected
      })
    clickAnalyticsEF(selected) 
    navigate('/performance')
  }

  if(!data?.dashboard?.energyFlow) 
    return (
      <Box my={1} ml={5} mr={3} width="100%" >
        <Skeleton variant="rectangular" 
          sx={{ borderRadius: '16px',  
          height: "50vh"
        }}/>
      </Box>
    )

  return (
    <Box id="energyFlowDiagram" 
      display="flex"
      flexDirection={"row"}
      justifyContent={"center"}
      width={"100%"}
      pl={9}>
      <div className={styles['energy-flow-container']} >
          <div className={styles['energy-flow-25d']}>
            <img className={styles['home-light-theme']} 
                  alt="" id="energyFlowHomeClick" 
                  onClick={()=>handleReportView('home')}
                  width="117px"
                  height="116px"
                  src="/images/dashboard/home-light-theme.svg" />
            <div className={styles['frame-parent']} id="energyFlowSolarClick" 
                  onClick={()=>handleReportView('solar')}>
              <div className={styles['frame-wrapper']}>
                <div>
                  <div className={styles['weather']}>
                    <div className={styles['weather-icon-container']}>
                      <div>{'' /* data?.dashboard?.energyFlow?.condition °C */}</div>
                    </div>
                    <div className={styles['temperature']}>
                      <span>{'' /* data?.dashboard?.energyFlow?.temperature */}</span>
                      <Typography variant="szBody.Regular.Small"></Typography>
                    </div>
                  </div>
                </div>
              </div>
              <img className={styles['solar-panel-light-theme']} alt=""
                src={`/images/dashboard/Solar${suffix?.solar || ''}.svg`}
              />
              <div className={styles['solar-stats-container']}>
                <div className={styles['mini-stats']}>
                  <div className={styles['text-parent']}>
                    <div className={styles['text']}>
                      <div className={styles['number']} id="energyFlowSolarValue">
                          {data?.dashboard?.energyFlow?.solar.value}
                      </div>
                      <div className={styles['unit']}>
                          {data?.dashboard?.energyFlow?.solar.unit}
                      </div>
                    </div>
                    <img className={styles['iconsetarrow-up']} alt="" />
                  </div>
                  <div className={styles['label-stack']}>
                    <div className={styles['label']}>Solar</div>
                    <img className={styles['iconsetchevron-right']}
                      alt=""
                      src="/images/dashboard/iconsetchevronright.svg"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles['grid-stats-container']}>
              <div className={styles['grid-stats-instance-wrapper']} 
                    id="energyFlowGridClick"
                    onClick={()=>handleReportView('grid')}>
                <div className={styles['grid-stats-instance']}>
                  <img className={styles['grid-light-theme']} 
                      alt="" 
                      src={`/images/dashboard/Grid${suffix?.grid || ''}.svg`}/>
                  <div className={styles['mini-stats1']}>
                    <div className={styles['text-group']}>
                      <div className={styles['text']}>
                        <div className={styles['number']} id="energyFlowGridValue">
                            {data?.dashboard?.energyFlow?.grid?.value}
                          </div>
                        <div className={styles['unit']}> 
                          {data?.dashboard?.energyFlow?.grid?.unit}
                        </div>
                      </div>
                      {/* {data?.dashboard?.energyFlow?.grid?.value > 0 ?
                        <img
                          className={styles['iconsetarrow-up1']}
                          alt=""
                          src="/images/dashboard/iconsetarrowup.svg"
                          style={{ 
                            transform: `rotate(${data?.dashboard?.energyFlow?.gridImport?'45deg':'180deg'})`}}
                        />
                        :<></>} */}
                    </div>
                    <div className={styles['label-stack1']}>
                      <div className={styles['label']}>Grid</div>
                      <img
                        className={styles['iconsetchevron-right1']}
                        alt=""
                        src="/images/dashboard/iconsetchevronright1.svg"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles['battery-stats-container']}>
                <div className={styles['mini-stats-wrapper']} 
                    id="energyFlowHomeClick2" 
                    onClick={()=>handleReportView('home')}>
                  <div className={styles['mini-stats2']}>
                    <div className={styles['text-container']}>
                      <div className={styles['text']}>
                        <div className={styles['number']} id="energyFlowHomeValue" >
                            {data?.dashboard?.energyFlow?.home?.value}
                        </div>
                        <div className={styles['unit']}> 
                          {data?.dashboard?.energyFlow?.home?.unit}
                        </div>
                      </div>
                      <img className={styles['iconsetarrow-up2']} alt="" />
                    </div>
                    <div className={styles['label-stack2']}>
                      <div className={styles['label']}>Home</div>
                      <img
                        className={styles['iconsetchevron-right2']}
                        alt=""
                        src="/images/dashboard/iconsetchevronright2.svg"
                      />
                    </div>
                  </div>
                </div>
                {/* Paths */}
                <div className={styles['paths-container']}>
                  {/* Solar -> Grid */}
                  <svg
                    width="168"
                    height="142"
                    viewBox="0 0 168 142"
                    fill="none"
                    className={`${styles['solar-grid']}`}>
                    <path
                      d="M36.3584 140.383L36.3584 68.0241C36.3584 60.8764 40.1757 54.2734 46.3709 50.7049L130.808 2.06792"
                      stroke="black"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={isFlowActive('solar', 'grid') ? `${styles['animated-path']}` : ''}
                      strokeDasharray={isFlowActive('solar', 'grid') ? '1 6' : undefined}
                      strokeOpacity={isFlowActive('solar', 'grid') ? undefined : '0.2'}
                    />
                  </svg>
                  {/* Solar -> Battery */}
                  <svg
                    width="161"
                    height="143"
                    viewBox="0 0 161 143"
                    fill="none"
                    className={`${styles['solar-battery']}`}>
                    <path
                      d="M119.465 137.67L119.465 58.559C119.465 51.4144 115.651 44.8137 109.46 41.2441L41.4364 2.02258"
                      stroke="black"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={isFlowActive('solar', 'battery') ? `${styles['animated-path']}` : ''}
                      strokeDasharray={isFlowActive('solar', 'battery') ? '1 6' : undefined}
                      strokeOpacity={isFlowActive('solar', 'battery') ? undefined : '0.2'}
                    />
                  </svg>
                  {/* Grid -> Battery */}
                  <svg
                    width="210"
                    height="180"
                    viewBox="0 0 210 180"
                    fill="none"
                    className={`${styles['grid-battery']}`}>
                    <path
                      d="M2.17143 89.0215L94.4262 142.598C102.093 147.051 111.56 147.061 119.239 142.624L198 97.1165"
                      stroke="black"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={gridBatteryFlowClass()}
                      strokeDasharray={isGridBatteryActive() ? '1 6' : undefined}
                      strokeOpacity={isGridBatteryActive() ? undefined : '0.2'}
                    />
                  </svg>
                  {/* Grid -> Home */}
                  <svg width="45" height="28" viewBox="0 0 45 28" 
                        fill="none" className={`${styles['grid-home']}`}>
                    <path
                      d="M1.575 25.8541 L42.1553 2.425"
                      stroke="black"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={isFlowActive('grid', 'home') ? `${styles['animated-path-reverse']}` : ''}
                      strokeDasharray={isFlowActive('grid', 'home') ? '1 6' : undefined}
                      strokeOpacity={isFlowActive('grid', 'home') ? undefined : '0.2'}
                    />
                  </svg>
                  {/* Solar -> Home */}
                  <svg width="5" height="50" viewBox="0 0 5 50" 
                      fill="none" className={`${styles['solar-home']}`}>
                    <path
                      d="M2.57094 1.57094 L2.57094 48.4291"
                      stroke="black"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={isFlowActive('solar', 'home') ? `${styles['animated-path-reverse']}` : ''}
                      strokeDasharray={isFlowActive('solar', 'home') ? '1 6' : undefined}
                      strokeOpacity={isFlowActive('solar', 'home') ? undefined : '0.2'}
                    />
                  </svg>
                  {/* Battery -> Home */}
                  <svg
                    width="45"
                    height="28"
                    viewBox="0 0 45 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`${styles['battery-home']}`}>
                    <path
                      d="M2.04904 2.45096 L42.7522 25.951"
                      stroke="black"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={isFlowActive('battery', 'home') ? `${styles['animated-path']}` : ''}
                      strokeDasharray={isFlowActive('battery', 'home') ? '1 6' : undefined}
                      strokeOpacity={isFlowActive('battery', 'home') ? undefined : '0.2'}
                    />
                  </svg>
                  <div className={styles['update-status']}>
                    <div className={styles['update-icon-container']}>
                      <div className={styles['div2']}></div>
                    </div>
                    <div className={styles['min-ago']}></div>
                  </div>
                </div>
              </div>
              <div className={styles['battery-icon-container']} 
                    id="energyFlowBatteryClick"
                    onClick={()=>handleReportView('battery')}>
                <div className={styles['battery-instance-container-parent']}>
                  <div className={styles['battery-instance-container']}>
                    <div className={styles['wrapper']}>
                      <div className={styles['div3']}>
                        {/* <span className={styles['span']}> */}
                          <img
                            alt=""
                            height={"20px"}
                            src="/images/Lightning.svg"
                          />
                        {/* </span> */}
                        <span className={styles['span1']} id="energyFlowBatteryCharged">
                            {data?.dashboard?.energyFlow?.battery?.charged}%
                        </span>
                      </div>
                    </div>
                    <animated.img
                      className={styles['battery-light-theme']}
                      alt=" "
                      width="39x"
                      height="82px"
                      src={imgs.src}
                      // src={`/images/dashboard/Battery${img?.batterySuffix || ''}${img?.batteryImg || '0'}.svg`}
                    />
                  </div>
                  <div className={styles['mini-stats3']}>
                    <div className={styles['frame-div']}>
                      <div className={styles['text']}>
                        {/* {data?.dashboard?.energyFlow?.battery?.value > 0 ?
                          <img
                            className={styles['iconsetarrow-up1']}
                            alt=""
                            src="/images/dashboard/iconsetarrowup.svg"
                            style={{ transform: `rotate(${data?.dashboard?.energyFlow?.batteryUsed?'315deg':'180deg'})`}}
                          />
                          :<></>} */}
                        <div className={styles['number']} 
                            id="energyFlowBatteryValue">
                            {data?.dashboard?.energyFlow?.battery?.value}
                        </div>
                        <div className={styles['unit']}> 
                          {data?.dashboard?.energyFlow?.battery?.unit}
                        </div>
                      </div>
                      <img className={styles['iconsetarrow-up3']} alt="" />
                    </div>
                    <div className={styles['label-stack3']}>
                      <div className={styles['label']}>Battery</div>
                      <img
                        className={styles['iconsetchevron-right3']}
                        alt=""
                        src="/images/dashboard/iconsetchevronright3.svg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
    </Box>
  )
}
